import React, { useState } from "react";
import { Heart, Flame, Pencil, Clock, MapPin, MessageCircle, CheckCircle2, Bell } from "lucide-react";
import { colors } from "@/lib/theme";
import { dayNum, formatTime, monthAbbr } from "@/lib/date";
import { FriendsGoing } from "@/components/peeps/FriendsGoing";

export function EventTicket({ ev, themeColor, themeOn, isTop, canEdit, canBlast, onToggleVote, onBlast, onEdit, onOpen }) {
  const [pop, setPop] = useState(false);

  const handleVote = (e) => {
    e.stopPropagation();
    setPop(true);
    setTimeout(() => setPop(false), 420);
    onToggleVote(ev.id);
  };

  return (
    <div
      data-testid={`event-${ev.id}`}
      onClick={() => onOpen?.(ev)}
      className="flex rounded-xl overflow-hidden cursor-pointer transition-all hover:border-[#4A4B5C]"
      style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}
    >
      {/* Date stub */}
      <div className="w-14 flex flex-col items-center justify-center py-3" style={{ background: themeColor }}>
        <span className="font-mono2 font-bold text-[10px] tracking-widest" style={{ color: themeOn }}>{monthAbbr(ev.date)}</span>
        <span className="font-mono2 font-bold text-[22px] leading-6" style={{ color: themeOn }}>{dayNum(ev.date)}</span>
      </div>

      {/* Perforation */}
      <div className="flex flex-col justify-evenly items-center" style={{ width: 1 }}>
        {Array.from({ length: 9 }).map((_, i) => (
          <div key={i} style={{ width: 1.5, height: 4, borderRadius: 1, background: colors.border }} />
        ))}
      </div>

      {/* Body */}
      <div className="flex-1 p-3 flex flex-col gap-[5px] min-w-0">
        <div className="flex justify-between items-start gap-2">
          <h3 className="flex-1 font-display font-bold text-[15.5px] line-clamp-2" style={{ color: colors.onSurface }}>{ev.title}</h3>
          <div className="flex items-center gap-1.5 shrink-0">
            {isTop && (
              <div className="flex items-center gap-[3px] py-[3px] px-[7px] rounded-full" style={{ background: colors.success + "22" }}>
                <Flame size={10} color={colors.success} />
                <span className="font-mono2 font-bold text-[8.5px] tracking-wide" style={{ color: colors.success }}>MOST WANTED</span>
              </div>
            )}
            {canEdit && (
              <button data-testid={`edit-event-${ev.id}`} onClick={(e) => { e.stopPropagation(); onEdit(ev); }} className="p-[5px] rounded-md" style={{ background: colors.surfaceTertiary, border: `1px solid ${colors.border}` }}>
                <Pencil size={13} color={colors.onSurfaceTertiary} />
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 flex-wrap">
          <Clock size={13} color={colors.onSurfaceTertiary} />
          <span className="font-display text-[12.5px]" style={{ color: colors.onSurfaceTertiary }}>{formatTime(ev.time)}{ev.end_time ? ` – ${formatTime(ev.end_time)}` : ""}</span>
          {!!ev.location && (
            <>
              <span style={{ color: colors.muted }}>·</span>
              <MapPin size={13} color={colors.onSurfaceTertiary} />
              <span className="font-display text-[12.5px] truncate max-w-[160px]" style={{ color: colors.onSurfaceTertiary }}>{ev.location}</span>
            </>
          )}
        </div>

        {!!ev.details && <p className="font-display text-[12.5px] line-clamp-2" style={{ color: colors.muted }}>{ev.details}</p>}

        {(ev.friends_going_count ?? 0) > 0 && (
          <div className="mt-0.5"><FriendsGoing friends={ev.friends_going} compact size={18} /></div>
        )}

        <div className="flex items-center justify-between mt-0.5">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {ev.source === "chat_import" && ev.source_label ? (
              <span className="font-display text-[11.5px] truncate" style={{ color: "#F0C060" }}>{ev.source_label}</span>
            ) : (
              <span className="font-display text-[11.5px] truncate" style={{ color: colors.faint }}>Added by {ev.creator?.display_name || "someone"}</span>
            )}
            {(ev.going_count ?? 0) > 0 && (
              <span className="flex items-center gap-[3px]">
                <CheckCircle2 size={11} color={colors.success} />
                <span className="font-mono2 font-medium text-[11px]" style={{ color: colors.success }}>{ev.going_count}</span>
              </span>
            )}
            {(ev.comment_count ?? 0) > 0 && (
              <span className="flex items-center gap-[3px]">
                <MessageCircle size={10} color={colors.onSurfaceTertiary} />
                <span className="font-mono2 font-medium text-[11px]" style={{ color: colors.onSurfaceTertiary }}>{ev.comment_count}</span>
              </span>
            )}
          </div>
          {!!ev.google_event_id && (
            <span className="flex items-center gap-[3px]">
              <CheckCircle2 size={11} color={colors.brandSecondary} />
              <span className="font-display font-medium text-[11px]" style={{ color: colors.brandSecondary }}>Synced</span>
            </span>
          )}
        </div>

        <div className="flex justify-end items-center gap-2 mt-1">
          {canBlast && (
            <button data-testid={`blast-event-${ev.id}`} onClick={(e) => { e.stopPropagation(); onBlast(ev); }} className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: colors.surfaceTertiary, border: `1px solid ${colors.border}` }}>
              <Bell size={15} color={colors.onSurfaceTertiary} />
            </button>
          )}
          <button
            data-testid={`vote-event-${ev.id}`}
            onClick={handleVote}
            className="flex items-center gap-1.5 rounded-full py-1.5 px-3.5"
            style={{ background: ev.voted ? themeColor : colors.surfaceTertiary, border: `1px solid ${ev.voted ? "transparent" : colors.border}` }}
          >
            <span className={pop ? "animate-heart" : ""} style={{ display: "inline-flex" }}>
              <Heart size={15} color={ev.voted ? themeOn : colors.onSurfaceTertiary} fill={ev.voted ? themeOn : "none"} />
            </span>
            <span className="font-mono2 font-medium text-[13px]" style={{ color: ev.voted ? themeOn : colors.onSurfaceTertiary }}>{ev.vote_count}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
